import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AIPlugin } from '../../aiPluginType';

import { getContentId } from './content-id';

type StandDownRecord = {
	dismissalTimestamps: number[];
};

export interface StandDownManagerOptions {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}

const DEFAULT_STORAGE_KEY_PREFIX = 'ai-quick-prompt-standdown';
const DEFAULT_MAX_DISMISSALS_IN_WINDOW = 1;
const DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

export class StandDownManager {
	private api: ExtractInjectionAPI<AIPlugin> | undefined;
	private storageKeyPrefix: string = DEFAULT_STORAGE_KEY_PREFIX;
	private maxDismissalsInWindow: number = DEFAULT_MAX_DISMISSALS_IN_WINDOW;
	private windowMs: number = DEFAULT_WINDOW_MS;

	constructor(options: StandDownManagerOptions) {
		this.api = options.api;
	}

	shouldStandDown(): boolean {
		const record = this.pruneExpired(this.readRecord());
		this.writeRecord(record);

		if (record.dismissalTimestamps.length >= this.maxDismissalsInWindow) {
			return true;
		}
		return false;
	}

	recordDismissal(): void {
		const record = this.readRecord();
		record.dismissalTimestamps.push(Date.now());
		this.writeRecord(record);
	}

	reset(): void {
		try {
			localStorage.removeItem(this.getStorageKey());
		} catch {
			// Silently ignore storage errors
		}
	}

	private getStorageKey(): string {
		return `${this.storageKeyPrefix}-${getContentId(this.api)}`;
	}

	private readRecord(): StandDownRecord {
		try {
			const raw = localStorage.getItem(this.getStorageKey());
			if (!raw) {
				return { dismissalTimestamps: [] };
			}
			return JSON.parse(raw) as StandDownRecord;
		} catch {
			return { dismissalTimestamps: [] };
		}
	}

	private writeRecord(record: StandDownRecord): void {
		try {
			localStorage.setItem(this.getStorageKey(), JSON.stringify(record));
		} catch {
			// Silently ignore storage errors (e.g., quota exceeded)
		}
	}

	private pruneExpired(record: StandDownRecord): StandDownRecord {
		const cutoff = Date.now() - this.windowMs;
		return {
			dismissalTimestamps: record.dismissalTimestamps.filter((ts) => ts > cutoff),
		};
	}
}
